/**
 * m3 echo Frontend - System Tab
 *
 * System settings including Early Access Disclaimer and Debug API Keys.
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  TextField,
  Switch,
  FormControlLabel,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Divider,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AddIcon from '@mui/icons-material/Add';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import EngineeringIcon from '@mui/icons-material/Engineering';
import EmailIcon from '@mui/icons-material/Email';
import SendIcon from '@mui/icons-material/Send';
import { settingsApi, debugApi } from '../../services/api';
import { DebugApiKey, DebugApiKeyCreated } from '../../types/api';

export const SystemTab: React.FC = () => {
  // System Settings State
  const [systemSettings, setSystemSettings] = useState<{
    id: number;
    disclaimer_enabled: boolean;
    disclaimer_headline: string;
    disclaimer_text: string;
    maintenance_mode: boolean;
    weekly_report_enabled: boolean;
    weekly_report_email: string | null;
    weekly_report_last_sent: string | null;
  } | null>(null);
  const [systemSettingsLoading, setSystemSettingsLoading] = useState(false);
  const [systemSettingsError, setSystemSettingsError] = useState<string | null>(null);
  const [systemSettingsSaving, setSystemSettingsSaving] = useState(false);
  const [systemSettingsSuccess, setSystemSettingsSuccess] = useState(false);

  // Weekly Report Test State
  const [testReportLoading, setTestReportLoading] = useState(false);
  const [testReportSuccess, setTestReportSuccess] = useState<string | null>(null);
  const [testReportError, setTestReportError] = useState<string | null>(null);

  // Debug API Keys State
  const [apiKeys, setApiKeys] = useState<DebugApiKey[]>([]);
  const [apiKeysLoading, setApiKeysLoading] = useState(false);
  const [apiKeysError, setApiKeysError] = useState<string | null>(null);

  // Create Key Dialog State
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [newKeyDescription, setNewKeyDescription] = useState('');
  const [newKeyAllowedIps, setNewKeyAllowedIps] = useState('');
  const [newKeyExpiresDays, setNewKeyExpiresDays] = useState<number | ''>('');
  const [createKeyLoading, setCreateKeyLoading] = useState(false);
  const [createKeyError, setCreateKeyError] = useState<string | null>(null);

  // Created Key Dialog State (shows full key once)
  const [createdKey, setCreatedKey] = useState<DebugApiKeyCreated | null>(null);
  const [keyCopied, setKeyCopied] = useState(false);

  // Delete Confirmation State
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [keyToDelete, setKeyToDelete] = useState<DebugApiKey | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    loadSystemSettings();
    loadApiKeys();
  }, []);

  const loadSystemSettings = async () => {
    setSystemSettingsLoading(true);
    setSystemSettingsError(null);
    try {
      const response = await settingsApi.getSystemSettings();
      setSystemSettings(response.data);
    } catch (error: any) {
      console.error('Fehler beim Laden der System-Einstellungen:', error);
      setSystemSettingsError(error.response?.data?.detail || 'Fehler beim Laden der System-Einstellungen');
    } finally {
      setSystemSettingsLoading(false);
    }
  };

  const handleUpdateSystemSettings = async () => {
    if (!systemSettings) return;

    setSystemSettingsSaving(true);
    setSystemSettingsError(null);
    setSystemSettingsSuccess(false);

    try {
      const response = await settingsApi.updateSystemSettings({
        disclaimer_enabled: systemSettings.disclaimer_enabled,
        disclaimer_headline: systemSettings.disclaimer_headline,
        disclaimer_text: systemSettings.disclaimer_text,
        maintenance_mode: systemSettings.maintenance_mode,
        weekly_report_enabled: systemSettings.weekly_report_enabled,
        weekly_report_email: systemSettings.weekly_report_email || undefined,
      });
      setSystemSettings(response.data);
      setSystemSettingsSuccess(true);
      setTimeout(() => setSystemSettingsSuccess(false), 3000);
    } catch (error: any) {
      console.error('Fehler beim Speichern der System-Einstellungen:', error);
      setSystemSettingsError(error.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setSystemSettingsSaving(false);
    }
  };

  const handleSendTestReport = async () => {
    setTestReportLoading(true);
    setTestReportSuccess(null);
    setTestReportError(null);
    try {
      const response = await settingsApi.sendTestWeeklyReport();
      setTestReportSuccess(`Test-Report gesendet an ${response.data.recipient}`);
      setTimeout(() => setTestReportSuccess(null), 5000);
    } catch (error: any) {
      setTestReportError(error.response?.data?.detail || 'Fehler beim Senden des Test-Reports');
      setTimeout(() => setTestReportError(null), 5000);
    } finally {
      setTestReportLoading(false);
    }
  };

  const loadApiKeys = async () => {
    setApiKeysLoading(true);
    setApiKeysError(null);
    try {
      const response = await debugApi.getKeys();
      setApiKeys(response.data);
    } catch (error: any) {
      console.error('Fehler beim Laden der API Keys:', error);
      setApiKeysError(error.response?.data?.detail || 'Fehler beim Laden der API Keys');
    } finally {
      setApiKeysLoading(false);
    }
  };

  const handleCreateKey = async () => {
    if (!newKeyName.trim()) return;

    setCreateKeyLoading(true);
    setCreateKeyError(null);

    try {
      const response = await debugApi.createKey({
        name: newKeyName.trim(),
        description: newKeyDescription.trim() || undefined,
        allowed_ips: newKeyAllowedIps.trim() || undefined,
        expires_in_days: newKeyExpiresDays || undefined,
      });
      setCreatedKey(response.data);
      setCreateDialogOpen(false);
      resetCreateForm();
      loadApiKeys();
    } catch (error: any) {
      console.error('Fehler beim Erstellen des API Keys:', error);
      setCreateKeyError(error.response?.data?.detail || 'Fehler beim Erstellen');
    } finally {
      setCreateKeyLoading(false);
    }
  };

  const resetCreateForm = () => {
    setNewKeyName('');
    setNewKeyDescription('');
    setNewKeyAllowedIps('');
    setNewKeyExpiresDays('');
    setCreateKeyError(null);
  };

  const handleDeleteKey = async () => {
    if (!keyToDelete) return;

    setDeleteLoading(true);
    try {
      await debugApi.deleteKey(keyToDelete.id);
      setDeleteDialogOpen(false);
      setKeyToDelete(null);
      loadApiKeys();
    } catch (error: any) {
      console.error('Fehler beim Löschen des API Keys:', error);
    } finally {
      setDeleteLoading(false);
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setKeyCopied(true);
      setTimeout(() => setKeyCopied(false), 2000);
    } catch (error) {
      console.error('Fehler beim Kopieren:', error);
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatLastUsed = (dateString: string | null) => {
    if (!dateString) return 'nie';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `vor ${diffMins} Min`;
    if (diffHours < 24) return `vor ${diffHours}h`;
    return `vor ${diffDays}d`;
  };

  return (
    <Box>
      {/* System Settings Section */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <SettingsIcon sx={{ mr: 1, fontSize: 28 }} />
        <Typography variant="h6">
          System-Einstellungen
        </Typography>
      </Box>

      {systemSettingsLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {systemSettingsError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {systemSettingsError}
        </Alert>
      )}

      {systemSettingsSuccess && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Einstellungen erfolgreich gespeichert!
        </Alert>
      )}

      {!systemSettingsLoading && systemSettings && (
        <Box sx={{ maxWidth: 600, mb: 6 }}>
          {/* Maintenance Mode Section */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <EngineeringIcon sx={{ mr: 1 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Wartungsmodus
            </Typography>
          </Box>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Im Wartungsmodus können nur Administratoren auf das System zugreifen.
            Alle anderen Benutzer sehen eine Wartungsseite.
          </Typography>

          <FormControlLabel
            control={
              <Switch
                checked={systemSettings.maintenance_mode}
                onChange={(e) =>
                  setSystemSettings({
                    ...systemSettings,
                    maintenance_mode: e.target.checked,
                  })
                }
              />
            }
            label="Wartungsmodus aktiviert"
            sx={{ mb: 2, display: 'block' }}
          />

          {systemSettings.maintenance_mode && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Wartungsmodus ist aktiv. Nur Admins haben Zugriff.
            </Alert>
          )}

          <Divider sx={{ my: 3 }} />

          {/* Weekly Report Section */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <EmailIcon sx={{ mr: 1 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Wöchentlicher Report
            </Typography>
          </Box>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Automatischer Statusbericht per E-Mail, jeden Montag um 9:00 Uhr (MEZ).
          </Typography>

          <FormControlLabel
            control={
              <Switch
                checked={systemSettings.weekly_report_enabled}
                onChange={(e) =>
                  setSystemSettings({
                    ...systemSettings,
                    weekly_report_enabled: e.target.checked,
                  })
                }
              />
            }
            label="Wöchentlichen Report aktivieren"
            sx={{ mb: 2, display: 'block' }}
          />

          <TextField
            fullWidth
            label="Report E-Mail-Adresse"
            type="email"
            value={systemSettings.weekly_report_email || ''}
            onChange={(e) =>
              setSystemSettings({
                ...systemSettings,
                weekly_report_email: e.target.value || null,
              })
            }
            placeholder="admin@example.com"
            helperText="Leer lassen um NOTIFICATION_EMAIL aus der Serverkonfiguration zu verwenden"
            sx={{ mb: 2 }}
          />

          {systemSettings.weekly_report_last_sent && (
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Letzter Report: {new Date(systemSettings.weekly_report_last_sent).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })}
            </Typography>
          )}

          <Button
            variant="outlined"
            startIcon={testReportLoading ? <CircularProgress size={16} /> : <SendIcon />}
            onClick={handleSendTestReport}
            disabled={testReportLoading}
            sx={{ mb: 1 }}
          >
            {testReportLoading ? 'Senden...' : 'Test-Report senden'}
          </Button>

          {testReportSuccess && (
            <Alert severity="success" sx={{ mt: 1, mb: 1 }}>
              {testReportSuccess}
            </Alert>
          )}

          {testReportError && (
            <Alert severity="error" sx={{ mt: 1, mb: 1 }}>
              {testReportError}
            </Alert>
          )}

          <Divider sx={{ my: 3 }} />

          {/* Early Access Disclaimer Section */}
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
            Early Access Disclaimer
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
            Konfigurieren Sie den Early Access Disclaimer, der Benutzern beim ersten Login
            und täglich beim ersten Login des Tages angezeigt wird.
          </Typography>

          <FormControlLabel
            control={
              <Switch
                checked={systemSettings.disclaimer_enabled}
                onChange={(e) =>
                  setSystemSettings({
                    ...systemSettings,
                    disclaimer_enabled: e.target.checked,
                  })
                }
              />
            }
            label="Disclaimer aktiviert"
            sx={{ mb: 3, display: 'block' }}
          />

          <TextField
            fullWidth
            label="Überschrift"
            value={systemSettings.disclaimer_headline}
            onChange={(e) =>
              setSystemSettings({
                ...systemSettings,
                disclaimer_headline: e.target.value,
              })
            }
            inputProps={{ maxLength: 255 }}
            helperText={`${systemSettings.disclaimer_headline.length}/255 Zeichen`}
            sx={{ mb: 3 }}
          />

          <TextField
            fullWidth
            multiline
            rows={4}
            label="Disclaimer Text"
            value={systemSettings.disclaimer_text}
            onChange={(e) =>
              setSystemSettings({
                ...systemSettings,
                disclaimer_text: e.target.value,
              })
            }
            helperText="Der Text, der im Disclaimer-Popup angezeigt wird"
            sx={{ mb: 3 }}
          />

          <Button
            variant="contained"
            onClick={handleUpdateSystemSettings}
            disabled={systemSettingsSaving}
          >
            {systemSettingsSaving ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                Speichern...
              </>
            ) : (
              'Einstellungen speichern'
            )}
          </Button>
        </Box>
      )}

      <Divider sx={{ my: 4 }} />

      {/* Debug API Keys Section */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <VpnKeyIcon sx={{ mr: 1, fontSize: 28 }} />
        <Typography variant="h6">
          Debug API Keys
        </Typography>
        <Tooltip title="API Keys für externen Log-Zugriff (z.B. Claude Code). Keys ermöglichen das Abrufen von Backend-Logs ohne Browser-Login.">
          <IconButton size="small" sx={{ ml: 1 }}>
            <HelpOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
        API Keys für externen Log-Zugriff (z.B. Claude Code)
      </Typography>

      {apiKeysError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {apiKeysError}
        </Alert>
      )}

      {apiKeysLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <TableContainer component={Paper} sx={{ mb: 2, overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Prefix</TableCell>
                  <TableCell>Genutzt</TableCell>
                  <TableCell>Gültig bis</TableCell>
                  <TableCell align="right">Aktionen</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {apiKeys.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                      <Typography color="textSecondary">
                        Keine API Keys vorhanden
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  apiKeys.map((key) => (
                    <TableRow key={key.id}>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {key.name}
                        </Typography>
                        {key.description && (
                          <Typography variant="caption" color="textSecondary">
                            {key.description}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <code style={{ fontSize: '0.85em' }}>{key.key_prefix}...</code>
                      </TableCell>
                      <TableCell>
                        <Tooltip title={key.use_count > 0 ? `${key.use_count} Aufrufe` : ''}>
                          <span>{formatLastUsed(key.last_used_at)}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        {key.expires_at ? (
                          new Date(key.expires_at) < new Date() ? (
                            <Typography color="error" variant="body2">
                              abgelaufen
                            </Typography>
                          ) : (
                            formatDate(key.expires_at)
                          )
                        ) : (
                          'unbegrenzt'
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Widerrufen">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => {
                              setKeyToDelete(key);
                              setDeleteDialogOpen(true);
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
          >
            Neuen API Key erstellen
          </Button>
        </>
      )}

      {/* Create Key Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => {
          setCreateDialogOpen(false);
          resetCreateForm();
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Neuen Debug API Key erstellen</DialogTitle>
        <DialogContent>
          {createKeyError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {createKeyError}
            </Alert>
          )}

          <TextField
            fullWidth
            label="Name"
            placeholder="z.B. Claude Code Debug"
            value={newKeyName}
            onChange={(e) => setNewKeyName(e.target.value)}
            sx={{ mt: 2, mb: 2 }}
            required
          />

          <TextField
            fullWidth
            label="Beschreibung"
            placeholder="Optional"
            value={newKeyDescription}
            onChange={(e) => setNewKeyDescription(e.target.value)}
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            label="Erlaubte IPs"
            placeholder="leer = alle IPs erlaubt"
            value={newKeyAllowedIps}
            onChange={(e) => setNewKeyAllowedIps(e.target.value)}
            helperText="Kommagetrennte IP-Adressen oder CIDR-Bereiche (z.B. 192.168.1.0/24)"
            sx={{ mb: 2 }}
          />

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Gültigkeit</InputLabel>
            <Select
              value={newKeyExpiresDays}
              label="Gültigkeit"
              onChange={(e) => setNewKeyExpiresDays(e.target.value as number | '')}
            >
              <MenuItem value="">Unbegrenzt</MenuItem>
              <MenuItem value={7}>7 Tage</MenuItem>
              <MenuItem value={30}>30 Tage</MenuItem>
              <MenuItem value={90}>90 Tage</MenuItem>
              <MenuItem value={365}>1 Jahr</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setCreateDialogOpen(false);
            resetCreateForm();
          }}>
            Abbrechen
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateKey}
            disabled={!newKeyName.trim() || createKeyLoading}
          >
            {createKeyLoading ? <CircularProgress size={20} /> : 'Erstellen'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Created Key Dialog (shows full key once) */}
      <Dialog
        open={!!createdKey}
        onClose={() => setCreatedKey(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningAmberIcon color="warning" />
          API Key erstellt - Jetzt kopieren!
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 3 }}>
            Dieser Key wird nur <strong>EINMAL</strong> angezeigt und kann nicht erneut abgerufen werden.
          </Alert>

          <Paper
            variant="outlined"
            sx={{
              p: 2,
              mb: 3,
              backgroundColor: 'grey.100',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <code style={{ fontSize: '0.9em', wordBreak: 'break-all', flex: 1 }}>
              {createdKey?.api_key}
            </code>
            <Tooltip title={keyCopied ? 'Kopiert!' : 'Kopieren'}>
              <IconButton
                onClick={() => createdKey && copyToClipboard(createdKey.api_key)}
                color={keyCopied ? 'success' : 'default'}
              >
                <ContentCopyIcon />
              </IconButton>
            </Tooltip>
          </Paper>

          <Typography variant="subtitle2" gutterBottom>
            Nutzung:
          </Typography>
          <Paper variant="outlined" sx={{ p: 2, backgroundColor: 'grey.900' }}>
            <code style={{ color: '#fff', fontSize: '0.8em', whiteSpace: 'pre-wrap' }}>
{`curl "https://api.../debug/logs?level=ERROR" \\
  -H "X-Debug-API-Key: ${createdKey?.api_key?.substring(0, 20)}..."`}
            </code>
          </Paper>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setCreatedKey(null)}>
            Schließen
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setKeyToDelete(null);
        }}
      >
        <DialogTitle>API Key widerrufen?</DialogTitle>
        <DialogContent>
          <Typography>
            Möchten Sie den API Key <strong>"{keyToDelete?.name}"</strong> wirklich widerrufen?
          </Typography>
          <Typography color="textSecondary" sx={{ mt: 1 }}>
            Diese Aktion kann nicht rückgängig gemacht werden. Alle Systeme, die diesen Key verwenden, verlieren sofort den Zugriff.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setDeleteDialogOpen(false);
            setKeyToDelete(null);
          }}>
            Abbrechen
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteKey}
            disabled={deleteLoading}
          >
            {deleteLoading ? <CircularProgress size={20} /> : 'Widerrufen'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SystemTab;
